<template>
  <div class="cohort-classes-details-component">
    <p v-if="cohort.description" class="description">{{ cohort.description }}</p>
    <div v-if="!cohortHasOverrides" class="no-override">
      <span v-if="cohort.isControl && !isDynamicAb">
        No treatment is applied to the control cohort: its users will have the same game configuration as if they were
        not part of the AB test.
      </span>
      <span v-if="isClassCohort && !cohort.isControl && !isDynamicAb">This cohort doesn't override any classes.</span>
    </div>

    <section v-if="isDynamicAb" class="cohort-treatments-wrapper">
      <div v-if="cohort.treatments.length === 0" class="cohort-no-treatment">No treatments applied</div>
      <draggable
        item-key="segmentId"
        :list="dynamicTreatments"
        :disabled="!canSortTreatment || isDraggingDisabled"
        :animation="150"
        ghost-class="ghost"
        @change="handleReOrder"
      >
        <template #item="{ element }">
          <div>
            <div class="draggable-header" :style="{ cursor: canSortTreatment ? 'move' : 'initial' }">
              <VdIcon
                v-if="canSortTreatment"
                store="fa"
                name="grip-lines"
                color="var(--vd-color-text-default)"
                class="grip-lines"
              ></VdIcon>
              <div class="cohort-treatment-title">
                <VdIcon store="fa" name="users" color="var(--vd-color-blue-default)"></VdIcon>
                {{ getSegmentName(element.segmentId) }}
              </div>
              <div class="cohort-size-change">
                <ConfigurationSizeChangeDisplay :gap-in-size="treatmentGapSizeMap.get(element.id)" />
              </div>
              <div class="cohort-treatment-id">
                TreatmentId:
                <span>{{ element.id }}</span>
              </div>
            </div>
            <section class="cohort-treatment-wrapper">
              <div class="cohort-treatment-overrides">
                <div class="cohort-treatment-overrides-title">Allocated shop</div>
                <div v-if="element.shopId">
                  <div class="cohort-treatment-overrides-shop">
                    <VdIcon store="far" name="shop" class="cohort-classe-icon" />
                    <span>
                      {{ getShopName(element.shopId) }}
                    </span>
                  </div>
                </div>
                <div v-else class="cohort-no-treatment">No treatment</div>
              </div>
              <div class="cohort-treatment-overrides">
                <div class="cohort-treatment-overrides-title">Offers</div>
                <div v-if="element.offers.length > 0">
                  <div class="cohort-treatment-overrides-shop">
                    <TreatmentOffersDisplay
                      v-if="flags.offerMultiLinkABTest.enabled"
                      :offer-ids="element.offers"
                      :offers-by-id="offersById"
                      :campaign-names-by-id="campaignNamesById"
                    />
                    <div v-else class="offer-list-wrapper">
                      <span v-for="offer in element.offers" :key="offer">
                        {{ getOfferName(offer) }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else class="cohort-no-treatment">No treatment</div>
              </div>
              <div class="cohort-treatment-overrides">
                <div
                  class="cohort-treatment-overrides-title clickable-overrides"
                  @click="onClassesClick(element.segmentId)"
                >
                  Classes
                  <VdIcon
                    store="fa"
                    name="arrow-up-right-from-square"
                    size="large"
                    color="var(--vd-color-blue-default)"
                  ></VdIcon>
                </div>
                <div v-if="element.classesOverrides.length > 0">
                  <div v-for="override in element.classesOverrides" :key="override.class.displayName">
                    <div class="cohort-classes-treatment-overrides-class">
                      <VdIcon store="fa" name="file-code" class="cohort-classe-icon" />
                      <span>
                        {{ override.class.technicalName }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else class="cohort-no-treatment">No classes selected</div>
              </div>
            </section>
          </div>
        </template>
      </draggable>
    </section>

    <el-collapse v-if="cohortHasOverrides && isClassCohort && !isDynamicAb" v-model="defaultExpanded">
      <el-collapse-item v-for="clazz in classes" :key="clazz.technicalName" :name="clazz.technicalName">
        <template #title>
          <section class="collapse-title">
            <vd-custom-icon name="classes-icon" class="classes-icon" width="16" />
            <h4 class="display-name">{{ clazz.displayName }}</h4>
          </section>
        </template>
        <section style="padding: 1rem">
          <el-table :data="clazz.attributes" stripe style="width: 100%" class="attributes-table single-instance">
            <el-table-column fixed prop="displayName" label="Display name" min-width="200px" show-overflow-tooltip />
            <el-table-column fixed prop="technicalName" label="Technical name" min-width="200px" />
            <el-table-column fixed prop="type" label="Type" width="100px" />
            <el-table-column
              :label="clazz.instances.length > 1 ? 'Instance 1 values' : 'Values'"
              prop="defaultValue"
              min-width="200px"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div
                  :class="{
                    overridden:
                      instanceValuesByClass[clazz.technicalName][clazz.instances[0].id][scope.row.technicalName]
                        .isOverridden,
                  }"
                >
                  <div v-if="scope.row.isArrayType" class="class-attribute-tags">
                    <span
                      v-for="(item, index) in instanceValuesByClass[clazz.technicalName][clazz.instances[0].id][
                        scope.row.technicalName
                      ].value || []"
                      :key="`${clazz.instances[0].id}-${scope.row.technicalName}-tags-${index}`"
                    >
                      {{ item }}
                    </span>
                  </div>
                  <JsonEditor
                    v-else-if="scope.row.type === 'json'"
                    :model-value="
                      instanceValuesByClass[clazz.technicalName][clazz.instances[0].id][scope.row.technicalName].value
                    "
                    readonly
                  />
                  <div v-else>
                    <template v-if="clazz.type === 'DEFAULT'">
                      {{
                        instanceValuesByClass[clazz.technicalName][clazz.instances[0].id][scope.row.technicalName].value
                      }}
                    </template>
                    <template v-else-if="clazz.type === 'SHOP'">
                      <VdIcon store="fa" name="store" color="var(--vd-color-blue-default)" size="large"></VdIcon>
                      {{
                        getShopName(
                          instanceValuesByClass[clazz.technicalName][clazz.instances[0].id][scope.row.technicalName]
                            .value
                        )
                      }}
                    </template>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="clazz.instances.length > 1" class="drawer-wrapper" @click="toggleDrawer">
            <VdIcon store="fa" :name="isDrawerOpen ? 'chevron-down' : 'chevron-right'" size="large" />
            <span>Multi instance view</span>
          </div>

          <el-table
            v-if="isDrawerOpen && clazz.instances.length > 1"
            :data="instanceIds(clazz.technicalName)"
            stripe
            style="width: 100%"
            class="attributes-table"
          >
            <el-table-column prop="" fixed label="Attribute name" width="220px">
              <template #default="attribute">
                <span class="instance">Instance {{ attribute.$index + 1 }}</span>
              </template>
            </el-table-column>

            <el-table-column
              v-for="attribute in clazz.attributes"
              :key="attribute.technicalName"
              :label="attribute.displayName"
              align="left"
              prop="value"
              min-width="200px"
              show-overflow-tooltip
            >
              <template #header>
                <div>
                  {{ attribute.displayName }}
                </div>
                <div class="class-attribute-technical-name">
                  {{ attribute.technicalName }}
                </div>
              </template>
              <template #default="scope">
                <div
                  :class="{
                    overridden:
                      instanceValuesByClass[clazz.technicalName][scope.row][attribute.technicalName].isOverridden,
                  }"
                >
                  <div v-if="attribute.isArrayType" class="class-attribute-tags">
                    <span
                      v-for="(item, index) in instanceValuesByClass[clazz.technicalName][scope.row][
                        attribute.technicalName
                      ].value || []"
                      :key="`${scope.row}-${attribute.technicalName}-tags-${index}`"
                    >
                      {{ item }}
                    </span>
                  </div>
                  <JsonEditor
                    v-else-if="attribute.type === 'json'"
                    :model-value="instanceValuesByClass[clazz.technicalName][scope.row][attribute.technicalName].value"
                    readonly
                  />
                  <div v-else>
                    <template v-if="clazz.type === 'DEFAULT'">
                      {{ instanceValuesByClass[clazz.technicalName][scope.row][attribute.technicalName].value }}
                    </template>
                    <template v-else-if="clazz.type === 'SHOP'">
                      <VdIcon store="fa" name="store" color="var(--vd-color-blue-default)" size="large"></VdIcon>
                      {{
                        getShopName(
                          instanceValuesByClass[clazz.technicalName][scope.row][attribute.technicalName].value
                        )
                      }}
                    </template>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </section>
      </el-collapse-item>
    </el-collapse>
  </div>
  <div class="cohort-classes-details-user-count">
    <VdIcon store="fa" name="users" size="small" />
    {{ cohort.userCount || 0 }}
    <span style="margin-left: 0.25rem">{{ pluralize("player", cohort.userCount || 0) }}</span>
    <span style="margin-left: 0.5rem"></span>
    <ConfigurationSizeChangeDisplay :gap-in-size="cohortTotalOverridesGap" />
  </div>
  <ClassOverridesDetails
    v-if="selectedSegment"
    :version-id="versionId"
    :segment-id="selectedSegment"
    :cohort-id="cohort.id"
    :abtest-id="abTestId"
    :game-id="gameId"
    :is-visible="isClassOverridesModalVisible"
    @close="handleModalClose"
  ></ClassOverridesDetails>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from "vue";
import { useRoute } from "vue-router";
import draggable from "vuedraggable";

import ConfigurationSizeChangeDisplay from "@/components/ConfigurationSizeChangeDisplay/ConfigurationSizeChangeDisplay.vue";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { pluralize } from "@/filters";
import {
  CohortFragment,
  CohortInput,
  CohortTreatment,
  useClassFormPageQuery,
  useCohortFormPageSaveMutation,
} from "@/graphql";
import { Shop } from "@/modules/Store/shop.types";
import { objectifyArray } from "@/utils";
import { getVTShopManagementClass } from "@/utils/getVTShopManagementClass";
import { mapCohortUpdatePayload } from "@/utils/mapCohortUpdatePayload";
import ClassOverridesDetails from "@/components/CohortDetails/ClassOverridesDetails.vue";
import TreatmentOffersDisplay from "@/components/CohortDetails/TreatmentOffers/TreatmentOffersDisplay.vue";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { TreatmentOfferSummary } from "@/modules/Offers/hooks/useTreatmentOffersData";

const props = defineProps({
  cohort: {
    type: Object as PropType<CohortFragment>,
    required: true,
  },
  abTestType: {
    type: String,
    default: "class",
  },
  shops: {
    type: Array as PropType<Shop[]>,
    default: () => [],
  },
  isDynamicAb: {
    type: Boolean,
    default: false,
  },
  dynamicSegments: {
    type: Array as PropType<{ id: string; name: string }[]>,
    default: () => [],
  },
  abTestId: {
    type: String,
    default: "",
  },
  abTestStatus: {
    type: String,
    required: true,
  },
  // Offer/campaign lookups fetched once at page level (useTreatmentOffersData); this component renders
  // per cohort, so fetching here would multiply requests by the number of cohorts.
  offersById: {
    type: Object as PropType<Record<string, TreatmentOfferSummary>>,
    default: () => ({}),
  },
  campaignNamesById: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
});

const isClassOverridesModalVisible = ref(false);
const selectedSegment = ref("");

const route = useRoute();
const gameId = ref(route.params.gameId as string);
const versionId = ref(route.params.versionId as string);
const isWipVersion = ref(route.params.versionId === "wip");
const classes = computed(() => (props.cohort.overrides ?? []).map((override) => override.class));

const defaultExpanded = ref(["VT.ShopManagementClass"]);
const isDraggingDisabled = ref(false);
const dynamicTreatments = computed(() =>
  props.cohort.treatments.map((treatment) => {
    const classesOverrides = treatment.classesOverrides?.filter(
      // VT.ShopManagementClass must be hidden in the UI
      (override) => override.class.technicalName !== "VT.ShopManagementClass"
    );
    return { ...treatment, classesOverrides };
  })
);

const classesById = computed(() => Object.fromEntries(classes.value.map((clazz) => [clazz.technicalName, clazz])));

const isClassCohort = computed(() => props.abTestType === "class" || props.abTestType === "shop");
const cohortHasOverrides = computed(() => props.cohort.overrides?.length !== 0);
/**
 * We browse the overrides and transform them into an object of objects(Record<string, unknown>[]), each one linked by an id
 * (class id, instance id, attribute technicalName)
 */
const instanceValuesByClass = computed(() =>
  objectifyArray(
    props.cohort.overrides.map((override) => ({
      technicalName: override.class.technicalName,
      instances: override.instances,
    })),
    ["technicalName", "instances", ["id", "values", ["technicalName", "*"]]]
  )
);

const { result: shopClass } = useClassFormPageQuery(
  {
    gameId: gameId.value,
    versionId: versionId.value,
    classId: "VT.ShopManagementClass",
  },
  { fetchPolicy: "network-only" }
);

// The grouped-by-mode/campaign offers display (TreatmentOffersDisplay) is behind a feature flag; the
// flag-off fallback below renders a plain list of offer names.
const { flags } = useFeatureFlags(gameId.value);

const shopManagementClassId = computed(() => shopClass.value?.game?.version?.class?.instances[0].id);

const canSortTreatment = computed(
  () => route.name === "listCohorts" && props.isDynamicAb && props.abTestStatus === "DRAFT" && isWipVersion.value
);

const instanceIds = (classId: string) => {
  const clazz = classesById.value[classId];

  const classInstanceIds = clazz.instances.map(({ id }) => id);

  const overridesInstanceIds =
    props.cohort.overrides
      .find((override) => override.class.technicalName === classId)
      ?.instances?.map(({ id }) => id) ?? [];

  return clazz.dynamicInstances ? overridesInstanceIds : classInstanceIds;
};

const getShopName = (shopId: string) => {
  const shopToDisplay = props.shops.find((shop) => shop.id === shopId);
  return shopToDisplay?.name ?? shopId;
};

const getSegmentName = (segmentId: string) => {
  const segment = props.dynamicSegments.find((seg) => seg.id === segmentId);
  return segment?.name;
};

const getTreatmentGapSize = (treatment: CohortTreatment) => {
  const treatmentSizeGap = treatment.classesOverrides
    ?.flatMap((override) => override.instances.flatMap((instance) => instance.values))
    .reduce((acc, value) => acc + (value.sizeGap ?? 0), 0);
  return treatmentSizeGap ?? 0;
};

const treatmentGapSizeMap = computed(() => {
  const intermediateMap = new Map<string, number>();
  props.cohort.treatments.forEach((treatment) => {
    intermediateMap.set(treatment.id, getTreatmentGapSize(treatment));
  });
  return intermediateMap;
});

const cohortTotalOverridesGap = computed(() => {
  if (props.isDynamicAb) {
    const allSizes = Array.from(treatmentGapSizeMap.value.values());
    if (allSizes.length === 0) {
      allSizes.push(0);
    }
    return Math.max(...allSizes);
  }
  let totalGap = 0;
  props.cohort.overrides.forEach((co) => {
    co.instances.forEach((instance) => {
      instance.values.forEach((value) => {
        totalGap += value.sizeGap ?? 0;
      });
    });
  });
  return totalGap;
});

const getOfferName = (offerId: string) => props.offersById[offerId]?.name ?? offerId;

/**
 * Save the cohort with the reordered treatments
 */
const { mutate: reorder } = useCohortFormPageSaveMutation({});

const handleReOrder = async () => {
  const treatments = [...dynamicTreatments.value];

  const treatmentsUpdatePayload = treatments.map((treatment, index) => ({
    ...treatment,
    rank: index + 1,
  }));

  isDraggingDisabled.value = true;

  const input: CohortInput = {
    name: props.cohort.name,
    description: props.cohort.description,
    allocationRate: props.cohort.allocationRate,
    isDynamic: true,
    treatments: [],
  };

  input.treatments = mapCohortUpdatePayload(treatmentsUpdatePayload as unknown as CohortTreatment[]).map(
    (treatment) => {
      if (treatment.shopId) {
        treatment.classesOverrides?.push(getVTShopManagementClass(treatment.shopId, shopManagementClassId.value));
      }

      return {
        ...treatment,
      };
    }
  );

  await reorder({
    gameId: gameId.value,
    versionId: versionId.value,
    abtestId: props.abTestId,
    cohortId: props.cohort.id,
    input,
  });

  isDraggingDisabled.value = false;
};

const isDrawerOpen = ref(false);

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

const onClassesClick = (segmentId: string) => {
  selectedSegment.value = segmentId;
  isClassOverridesModalVisible.value = true;
};

const handleModalClose = () => {
  isClassOverridesModalVisible.value = false;
  selectedSegment.value = "";
};
</script>

<style lang="scss">
.cohort-classes-details-component {
  color: var(--vd-color-text-default);

  .attributes-table {
    &.single-instance {
      &.el-table {
        margin-top: -1rem !important;
      }

      .el-table__body td.el-table__cell:first-child {
        background: white !important;
        border-right: none;
      }
    }
  }

  .el-collapse-item__content {
    padding-bottom: 0;
  }

  .el-table {
    color: var(--vd-color-text-default);
    border: 1px solid var(--vd-color-text-light-7);
    border-bottom: 1px;
    margin-top: 1rem !important;

    th.el-table__cell.is-leaf {
      background: var(--vd-color-text-light-9) !important;
    }

    .el-table__body td.el-table__cell:first-child {
      background: var(--vd-color-text-light-9) !important;
      border-right: 1px solid var(--vd-color-text-light-7);
      font-weight: 700;
    }
  }

  .el-table thead {
    color: var(--vd-color-text-default);
    font-weight: 700;

    .class-attribute-technical-name {
      font-size: font("S");
      color: var(--vd-color-text-light-2);
      margin-top: -4px;
      font-weight: 500;
    }
  }

  .draggable-header {
    display: flex;
    align-items: center;

    .grip-lines {
      margin-right: 0.5rem;
      height: 10px;
    }

    .ghost {
      opacity: 0.5;
      background: #c8ebfb;
    }
  }

  .cohort-treatment-wrapper {
    display: flex;
    margin-bottom: 1rem;
  }

  .cohort-treatment-overrides {
    width: 240px;
    margin-right: 2rem;

    .cohort-treatment-overrides-shop {
      display: flex;
      align-items: center;

      .offer-list-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .cohort-classe-icon {
        margin-right: 0.5rem;
      }

      // Direct child only: the allocated-shop icon. Avoid coloring the nested offers-grouping mode icons
      // (TreatmentOffersDisplay), which set their own mode colors.
      > svg {
        color: var(--vd-color-blue-default);
      }

      span {
        font-size: 12px;
      }
    }
  }

  .cohort-treatments-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .cohort-treatment-id {
      margin-left: 0.5rem;
      font-size: 10px;

      span {
        font-family: monospace, sans-serif;
      }
    }

    .cohort-treatment-title {
      display: flex;
      align-items: center;
      background-color: var(--vd-color-blue-light-9);
      border-radius: 0.5rem;
      margin: 0.5rem 0;
      padding: 0.25rem 0.5rem;
      font-size: 12px;
      width: fit-content;

      svg {
        height: 10px;
        margin-right: 0.5rem;
      }
    }

    .cohort-classes-treatment-overrides-class {
      display: flex;
      align-items: center;
      color: var(--vd-color-blue-default);
      font-size: 12px;
      margin-bottom: 0.5rem;

      .cohort-classe-icon {
        margin-right: 0.5rem;
      }
    }

    .cohort-treatment-overrides-title {
      margin-bottom: 0.5rem;
    }

    .clickable-overrides {
      cursor: pointer;
      display: flex;
      align-items: center;

      &:hover {
        cursor: pointer;
      }

      svg {
        margin-left: 0.5rem;
      }
    }

    .cohort-no-treatment {
      font-size: 12px;
      color: var(--vd-color-text-light-2);
    }
  }
  .offer-id {
    margin-left: 1rem;
    color: var(--vd-color-text-light-2);
  }
  .collapse-title {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;

    .classes-icon {
      fill: $grey;
      margin-left: size("M");
      margin-right: size("M");
    }
  }

  .class-attribute-tags {
    @extend %tags;
    margin-top: size("S");
    justify-content: flex-start;
  }

  .overridden:not(.is-default-value) > div:not(.class-attribute-tags) {
    display: inline-block;
    background-color: $cloudy-blue;

    .json-value {
      background-color: $cloudy-blue;
      border-color: $light-blue;
    }
  }

  .overridden:not(.is-default-value) .class-attribute-tags > span {
    background-color: $cloudy-blue;
    border-color: $light-blue;
  }

  .description {
    white-space: pre;
    margin-top: 0;
  }

  .no-override {
    font-style: italic;
    font-size: font("S");
  }

  .drawer-wrapper {
    margin: 1rem 0;
    cursor: pointer;

    svg {
      margin-right: 0.5rem;
      font-size: 12px;
    }

    span {
      margin-right: 0.5rem;
      font-weight: 700;
      color: var(--vd-color-text-default);
    }
  }
}

.cohort-classes-details-user-count {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 12px;
  margin-top: 0.5rem;

  svg {
    margin-right: 0.25rem;
  }
}

.cohort-size-change {
  margin-left: 5px;
}
</style>
